import { Avatar, Icons, Spinner } from "@dms/ui";
import { Suspense, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { notificationsApi } from "../lib/notificationsApi";
import { chatApi } from "../lib/chatApi";
import { NAV_ITEMS, NAV_SECTIONS } from "../nav";
import "./AppLayout.css";
import { LayoutWidthContext } from "./LayoutWidthContext";
import { OnboardingProvider } from "./OnboardingWizard";
import { NotificationsPopover } from "./NotificationsPopover";

const SIDEBAR_W = "var(--layout-sidebar-w)";
const SIDEBAR_COLLAPSED_W = "var(--layout-sidebar-collapsed-w)";

// Заголовок сторінки в хедері: за активним пунктом меню або спец-маршрутом.
function pageTitle(pathname: string): string {
  if (pathname === "/" || pathname.startsWith("/dashboard")) return "Панель";
  if (pathname.startsWith("/notifications")) return "Сповіщення";
  if (pathname.startsWith("/profile")) return "Мій профіль";
  if (pathname.startsWith("/settings")) return "Налаштування";
  const item = NAV_ITEMS.find((i) => pathname.startsWith(i.path));
  return item?.label ?? "Bukovel-Документообіг";
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [refreshNonce, setRefreshNonce] = useState(0);
  // Full-bleed робоча область (сторінка вмикає через useContentWidth) — дата-щільні
  // екрани (канбан) використовують усю ширину; решта — reading-cap.
  const [wide, setWide] = useState(false);
  const canUseChat = Boolean(user?.roles?.some((role) => role !== "AUDITOR"));
  const isChat = location.pathname.startsWith("/chat");
  const sidebarCollapsed = collapsed && !mobileNavOpen;

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileNavOpen]);

  // Лічильник непрочитаних: на mount, при зміні маршруту і після mark-all
  // (fail-soft → 0). `active`-cleanup відсікає stale-відповіді (нав-race).
  useEffect(() => {
    let active = true;
    notificationsApi
      .unreadCount()
      .then((count) => {
        if (active) setUnreadCount(count);
      })
      .catch(() => {
        if (active) setUnreadCount(0);
      });
    return () => {
      active = false;
    };
  }, [location.pathname, refreshNonce]);

  // M18 badge: lightweight polling every ~30s; muted conversations still count.
  useEffect(() => {
    if (!canUseChat) {
      setChatUnreadCount(0);
      return;
    }
    let active = true;
    const load = () => {
      void chatApi
        .unreadSummary()
        .then((summary) => {
          if (active) setChatUnreadCount(summary.totalUnread);
        })
        .catch(() => {
          if (active) setChatUnreadCount(0);
        });
    };
    load();
    const timer = window.setInterval(load, 30_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [canUseChat, location.pathname]);

  const handleLogout = async () => {
    setUserMenu(false);
    await logout();
    navigate("/login", { replace: true });
  };

  const fullName = user?.full_name ?? "Користувач";
  const role = user?.roles?.[0] ?? "";
  const title = pageTitle(location.pathname);

  return (
    <div className="app-shell" style={{ display: "flex", height: "100vh", overflow: "hidden", background: "var(--color-surface-default)" }}>
      {mobileNavOpen && (
        <button
          type="button"
          className="app-shell__scrim"
          aria-label="Закрити головне меню"
          onClick={() => setMobileNavOpen(false)}
        />
      )}
      {/* ── Sidebar ─────────────────────────────────────────── */}
      <aside
        className={`app-shell__sidebar${mobileNavOpen ? " is-open" : ""}`}
        style={{
          width: sidebarCollapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_W,
          flex: "none",
          background: "var(--color-surface-sidebar)",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          transition: "width var(--duration-base) var(--ease-standard)",
          height: "100%",
        }}
      >
        <div
          style={{
            height: "var(--layout-header-h)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: sidebarCollapsed ? "0 14px" : "0 18px",
            borderBottom: "1px solid var(--color-border-on-dark)",
            flex: "none",
          }}
        >
          <img
            src={sidebarCollapsed ? "/brand/clearflow-mark-white.png" : "/brand/clearflow-logo-horizontal-white.png"}
            alt="ClearFlow"
            style={{ height: 26, objectFit: "contain" }}
          />
        </div>

        <nav
          aria-label="Головна навігація"
          style={{ flex: 1, padding: "10px 10px", display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}
        >
          {NAV_SECTIONS.map((section, si) => (
            <div key={si} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {section.heading &&
                (sidebarCollapsed ? (
                  <div style={{ height: 1, background: "var(--color-border-on-dark)", margin: "10px 6px 8px" }} />
                ) : (
                  <div
                    style={{
                      padding: "14px 12px 4px",
                      fontSize: 10.5,
                      fontWeight: "var(--fw-semibold)",
                      letterSpacing: "var(--ls-caps)",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.42)",
                    }}
                  >
                    {section.heading}
                  </div>
                ))}
              {section.items
                .filter((item) => item.path !== "/chat" || canUseChat)
                .map((item) => {
                const Icon = Icons[item.icon];
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileNavOpen(false)}
                    title={item.label}
                    style={({ isActive }) => ({
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: sidebarCollapsed ? "10px 0" : "9px 12px",
                      justifyContent: sidebarCollapsed ? "center" : "flex-start",
                      borderRadius: "var(--radius-md)",
                      textDecoration: "none",
                      background: isActive ? "rgba(255,255,255,0.14)" : "transparent",
                      color: isActive ? "#fff" : "rgba(255,255,255,0.72)",
                      fontSize: "var(--fs-nav)",
                      fontWeight: isActive ? "var(--fw-semibold)" : "var(--fw-medium)",
                      position: "relative",
                    })}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span
                            style={{
                              position: "absolute",
                              left: 0,
                              top: 8,
                              bottom: 8,
                              width: 3,
                              background: "var(--teal-300)",
                              borderRadius: "0 2px 2px 0",
                            }}
                          />
                        )}
                        <Icon size={20} />
                        {!sidebarCollapsed && <span style={{ flex: 1 }}>{item.label}</span>}
                        {item.path === "/chat" && chatUnreadCount > 0 && (
                          <span
                            aria-label={`${chatUnreadCount} непрочитаних повідомлень`}
                            style={{
                              minWidth: 20,
                              height: 20,
                              padding: "0 6px",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "var(--radius-pill)",
                              background: "var(--teal-300)",
                              color: "var(--neutral-900)",
                              fontSize: 10,
                              fontWeight: "var(--fw-bold)",
                            }}
                          >
                            {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User footer + меню */}
        <div style={{ position: "relative", borderTop: "1px solid var(--color-border-on-dark)", flex: "none" }}>
          {userMenu && (
            <>
              <div onClick={() => setUserMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 1 }} />
              <div
                role="menu"
                style={{
                  position: "absolute",
                  bottom: "calc(100% + 6px)",
                  left: 10,
                  right: 10,
                  background: "#fff",
                  color: "var(--color-text-primary)",
                  border: "1px solid var(--color-border-default)",
                  borderRadius: "var(--radius-lg)",
                  boxShadow: "var(--shadow-popover)",
                  padding: 6,
                  zIndex: 2,
                }}
              >
                <RouterLink
                  role="menuitem"
                  to="/profile"
                  onClick={() => setUserMenu(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: "var(--radius-md)",
                    color: "var(--color-text-primary)",
                    textDecoration: "none",
                    fontSize: "var(--fs-body-sm)",
                  }}
                >
                  <Icons.user size={17} /> Мій профіль
                </RouterLink>
                <RouterLink
                  role="menuitem"
                  to="/settings"
                  onClick={() => setUserMenu(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: "var(--radius-md)",
                    color: "var(--color-text-primary)",
                    textDecoration: "none",
                    fontSize: "var(--fs-body-sm)",
                  }}
                >
                  <Icons.lock size={17} /> Зміна пароля
                </RouterLink>
                <RouterLink
                  role="menuitem"
                  to="/settings/notifications"
                  onClick={() => setUserMenu(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: "var(--radius-md)",
                    color: "var(--color-text-primary)",
                    textDecoration: "none",
                    fontSize: "var(--fs-body-sm)",
                  }}
                >
                  <Icons.settings size={17} /> Налаштування сповіщень
                </RouterLink>
                <button
                  role="menuitem"
                  type="button"
                  onClick={() => void handleLogout()}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    border: "none",
                    background: "transparent",
                    borderRadius: "var(--radius-md)",
                    cursor: "pointer",
                    fontSize: "var(--fs-body-sm)",
                    color: "var(--color-status-error)",
                    fontFamily: "var(--font-sans)",
                    textAlign: "left",
                  }}
                >
                  <Icons.lock size={17} /> Вийти
                </button>
              </div>
            </>
          )}
          <button
            type="button"
            aria-label="Меню користувача"
            onClick={() => setUserMenu((v) => !v)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: sidebarCollapsed ? "12px 0" : "12px 16px",
              justifyContent: sidebarCollapsed ? "center" : "flex-start",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#fff",
              fontFamily: "var(--font-sans)",
              textAlign: "left",
            }}
          >
            <Avatar name={fullName} src={user?.avatar_url ?? undefined} size={sidebarCollapsed ? 28 : 34} />
            {!sidebarCollapsed && (
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: "var(--fs-body-sm)", fontWeight: "var(--fw-semibold)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {fullName}
                </span>
                {role && (
                  <span style={{ display: "block", fontSize: "var(--fs-meta)", color: "rgba(255,255,255,0.6)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {role}
                  </span>
                )}
              </span>
            )}
            {!sidebarCollapsed && <Icons.settings size={16} style={{ color: "rgba(255,255,255,0.6)" }} />}
          </button>
        </div>

        <button
          className="app-shell__collapse"
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? "Розгорнути меню" : "Згорнути меню"}
          style={{
            height: 40,
            flex: "none",
            border: "none",
            borderTop: "1px solid var(--color-border-on-dark)",
            background: "transparent",
            color: "rgba(255,255,255,0.6)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            fontSize: "var(--fs-body-sm)",
            fontFamily: "var(--font-sans)",
          }}
        >
          {collapsed ? <Icons.chevronRight size={18} /> : <Icons.chevronLeft size={18} />}
          {!collapsed && <span>Згорнути</span>}
        </button>
      </aside>

      {/* ── Main column ─────────────────────────────────────── */}
      <div className="app-shell__main" style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <header
          className="app-shell__header"
          style={{
            height: "var(--layout-header-h)",
            flex: "none",
            background: "var(--color-surface-header)",
            borderBottom: "1px solid var(--color-border-default)",
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: "0 20px",
            position: "relative",
          }}
        >
          <button
            type="button"
            className="app-shell__mobile-menu"
            aria-label="Відкрити головне меню"
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen(true)}
          >
            <Icons.menu size={21} />
          </button>
          <h1
            style={{
              flex: 1,
              minWidth: 0,
              margin: 0,
              fontSize: "var(--fs-h3)",
              lineHeight: "var(--lh-h3)",
              fontWeight: "var(--fw-semibold)",
              color: "var(--color-text-primary)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </h1>

          <button
            className="app-shell__search"
            type="button"
            aria-label="Пошук"
            onClick={() => navigate("/search")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              height: 38,
              width: 280,
              padding: "0 12px",
              background: "var(--neutral-0)",
              border: "1px solid var(--color-border-default)",
              borderRadius: "var(--radius-md)",
              color: "var(--color-text-tertiary)",
              cursor: "pointer",
              fontSize: "var(--fs-body)",
              fontFamily: "var(--font-sans)",
            }}
          >
            <Icons.search size={18} />
            <span className="app-shell__search-label">Глобальний пошук…</span>
          </button>

          <button
            className="app-shell__create"
            type="button"
            aria-label="Створити"
            onClick={() => navigate("/create")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              height: 38,
              padding: "0 14px",
              background: "var(--color-brand-primary)",
              color: "#fff",
              border: "none",
              borderRadius: "var(--radius-md)",
              fontSize: "var(--fs-button)",
              fontWeight: "var(--fw-semibold)",
              fontFamily: "var(--font-sans)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <Icons.plus size={18} /> <span className="app-shell__create-label">Створити</span>
          </button>

          {/* Швидкий вхід у довідку (M20, ADR-042 §6e — header ?-affordance). */}
          <RouterLink
            to="/help"
            aria-label="Довідка"
            title="Довідка"
            className="app-shell__help"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 38,
              height: 38,
              flex: "none",
              border: "1px solid transparent",
              borderRadius: "var(--radius-md)",
              background: "transparent",
              color: "var(--color-text-secondary)",
              textDecoration: "none",
            }}
          >
            <Icons.helpCircle size={20} />
          </RouterLink>

          <span style={{ position: "relative", display: "inline-flex" }}>
            <button
              className="app-shell__notifications"
              ref={bellRef}
              type="button"
              aria-label="Сповіщення"
              onClick={() => setNotifOpen((v) => !v)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 38,
                height: 38,
                border: "1px solid transparent",
                borderRadius: "var(--radius-md)",
                background: notifOpen ? "var(--color-surface-selected)" : "transparent",
                color: notifOpen ? "var(--color-brand-primary)" : "var(--color-text-secondary)",
                cursor: "pointer",
              }}
            >
              <Icons.bell size={20} />
            </button>
            {unreadCount > 0 && (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  top: 2,
                  right: 0,
                  minWidth: 16,
                  height: 16,
                  padding: "0 4px",
                  borderRadius: "var(--radius-pill)",
                  background: "var(--color-status-error)",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: "var(--fw-semibold)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid #fff",
                  pointerEvents: "none",
                }}
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </span>
          {notifOpen && bellRef.current && (
            <NotificationsPopover
              anchorEl={bellRef.current}
              onClose={() => setNotifOpen(false)}
              onChanged={() => setRefreshNonce((n) => n + 1)}
            />
          )}
        </header>

        <main
          className={`app-shell__content${isChat ? " is-chat" : ""}`}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: isChat ? "hidden" : "auto",
            padding: isChat ? 0 : "24px 28px",
          }}
        >
          <OnboardingProvider>
          <LayoutWidthContext.Provider value={{ wide, setWide }}>
            {/* Робоча область — завжди full-bleed (вся ширина), без reading-cap: усі
                сторінки однакові, без стрибка «центровано ↔ на повну ширину» між
                вкладками/видами (запит власника). useContentWidth лишається no-op. */}
            <div
              className="app-shell__outlet"
              style={{
                maxWidth: "none",
                width: "100%",
                height: isChat ? "100%" : undefined,
                margin: 0,
              }}
            >
              {/* TD-0047: межа Suspense навколо Outlet — lazy-сторінки вантажаться
                  окремими chunk-ами, shell (sidebar/header) лишається видимим. */}
              <Suspense
                fallback={
                  <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
                    <Spinner />
                  </div>
                }
              >
                <Outlet />
              </Suspense>
            </div>
          </LayoutWidthContext.Provider>
          </OnboardingProvider>
        </main>
      </div>
    </div>
  );
}
